import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, Loader2, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { WaitlistDetailResponse } from '../types';
import { waitlistApi } from '../services/api';
import { useWaitlistAnalytics } from '../hooks/useAnalytics';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';
import Tooltip from './Tooltip';
import { vibrateSuccess, vibrateNotification, vibrateWarning, vibrateLight } from '../utils/haptics';
import { playSound, initializeAudio } from '../utils/sound';
import { extractApiError } from '../utils/errorUtils'; // Issue #964: Standardized error handling
import { parseDateOnly } from '../utils/fairStatus'; // Issue #1672

interface SlotOfferModalProps {
  entry: WaitlistDetailResponse;
  isOpen: boolean;
  onClose: () => void;
  onAccept?: () => void;
  onDecline?: () => void;
}

/**
 * SlotOfferModal - Modal for vendors to accept or decline a slot offer
 * Shows countdown timer and offer details
 *
 * Accessibility (Issue #499):
 * - Respects prefers-reduced-motion
 * - Skip link for modal content
 * - Keyboard navigation (Tab, Enter, Escape)
 * - ARIA labels and roles
 */
function SlotOfferModal({ entry, isOpen, onClose, onAccept, onDecline }: SlotOfferModalProps) {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [isAccepting, setIsAccepting] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [modalOpenTime] = useState(Date.now()); // Track time modal was opened (Issue #498)
  const [highUrgencyTracked, setHighUrgencyTracked] = useState(false); // Track if high urgency event fired (Issue #498)
  const [criticalUrgencyTracked, setCriticalUrgencyTracked] = useState(false); // Track if critical urgency event fired (Issue #498)

  // Analytics tracking (Issue #498)
  const {
    trackSlotOfferViewed,
    trackSlotOfferAccepted,
    trackSlotOfferDeclined,
    trackSlotOfferModalOpened,
    trackCountdownUrgencyReached,
    trackError,
  } = useWaitlistAnalytics();

  // Calculate countdown details with visual states
  const countdownDetails = useMemo(() => {
    if (!entry.offer_expires_at) {
      return { text: 'N/A', colorClass: 'gray', progress: 0, isExpired: true, isUrgent: false };
    }

    const now = new Date();
    const expiresAt = new Date(entry.offer_expires_at);
    const diff = expiresAt.getTime() - now.getTime();

    if (diff <= 0) {
      return { text: 'Expired', colorClass: 'red', progress: 100, isExpired: true, isUrgent: false };
    }

    const totalHours = diff / (1000 * 60 * 60);
    const hours = Math.floor(totalHours);
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    // Calculate offer duration dynamically from notified_at and offer_expires_at timestamps (Issue #640)
    // Falls back to 48 hours if notified_at is not available
    let offerDurationMs = 48 * 60 * 60 * 1000; // Default: 48 hours
    if (entry.notified_at) {
      const notifiedAt = new Date(entry.notified_at);
      offerDurationMs = expiresAt.getTime() - notifiedAt.getTime();
    }
    const progress = Math.max(0, Math.min(100, ((offerDurationMs - diff) / offerDurationMs) * 100));

    let text: string;
    let colorClass: string;
    let isUrgent = false;

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;
      text = `${days} day${days > 1 ? 's' : ''}${remainingHours > 0 ? `, ${remainingHours} hour${remainingHours > 1 ? 's' : ''}` : ''} remaining`;
      colorClass = 'green';
    } else if (hours >= 6) {
      text = `${hours} hour${hours > 1 ? 's' : ''} remaining`;
      colorClass = 'yellow';
    } else if (hours >= 1) {
      text = `${hours} hour${hours > 1 ? 's' : ''}, ${minutes} minute${minutes !== 1 ? 's' : ''} remaining`;
      colorClass = 'red';
      isUrgent = true;
    } else {
      text = `${minutes} minute${minutes !== 1 ? 's' : ''} remaining!`;
      colorClass = 'red';
      isUrgent = true;
    }

    return { text, colorClass, progress, isExpired: false, isUrgent };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.offer_expires_at, timeRemaining]);

  // Track modal opened and slot offer viewed (Issue #498)
  useEffect(() => {
    if (isOpen && entry.offer_expires_at) {
      const now = new Date();
      const expiresAt = new Date(entry.offer_expires_at);
      const diff = expiresAt.getTime() - now.getTime();
      const totalHours = diff / (1000 * 60 * 60);

      trackSlotOfferModalOpened({
        fair_id: entry.fair_id,
        time_remaining_hours: totalHours,
      });

      trackSlotOfferViewed({
        fair_id: entry.fair_id,
        position: entry.position,
        time_remaining_hours: totalHours,
        urgency_level: totalHours < 6 ? 'high' : 'normal',
      });

      // Play notification sound and haptic when modal opens (Issue #500)
      initializeAudio(); // Initialize audio context on user interaction
      playSound('notification');
      vibrateNotification();
    }
  }, [isOpen, entry.fair_id, entry.position, entry.offer_expires_at, trackSlotOfferModalOpened, trackSlotOfferViewed]);

  // Countdown timer
  useEffect(() => {
    if (!isOpen || !entry.offer_expires_at) return;

    const updateTimer = () => {
      setTimeRemaining(prev => prev === '' ? 'tick' : '');
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [isOpen, entry.offer_expires_at]);

  // Track urgency level reached with flags to prevent missing events (Issue #498)
  useEffect(() => {
    if (!isOpen || !entry.offer_expires_at) return;

    const checkUrgency = () => {
      const now = new Date();
      const expiresAt = new Date(entry.offer_expires_at);
      const diff = expiresAt.getTime() - now.getTime();
      const totalHours = diff / (1000 * 60 * 60);

      // Track high urgency (< 6 hours) - fire once
      if (totalHours <= 6 && !highUrgencyTracked) {
        trackCountdownUrgencyReached({
          fair_id: entry.fair_id,
          urgency_level: 'high',
        });
        setHighUrgencyTracked(true);
        // Warning feedback for high urgency (Issue #500)
        playSound('warning');
        vibrateWarning();
      }

      // Track critical urgency (< 1 hour) - fire once
      if (totalHours <= 1 && !criticalUrgencyTracked) {
        trackCountdownUrgencyReached({
          fair_id: entry.fair_id,
          urgency_level: 'critical',
        });
        setCriticalUrgencyTracked(true);
        // Critical warning feedback (Issue #500)
        playSound('warning');
        vibrateWarning();
      }
    };

    // Check immediately on mount, then every minute
    checkUrgency();
    const interval = setInterval(checkUrgency, 60000);
    return () => clearInterval(interval);
  }, [isOpen, entry.fair_id, entry.offer_expires_at, highUrgencyTracked, criticalUrgencyTracked, trackCountdownUrgencyReached]);

  const handleClose = useCallback(() => {
    setShowDeclineForm(false);
    setDeclineReason('');
    setError(null);
    setSuccess(false);
    setIsAccepting(false);
    setIsDeclining(false);
    onClose();
  }, [onClose]);

  const handleAccept = useCallback(async () => {
    try {
      setError(null);
      setIsAccepting(true);

      // Light haptic on click (Issue #500)
      vibrateLight();

      // Calculate time to decision (Issue #498)
      const timeToDecision = Date.now() - modalOpenTime;
      const now = new Date();
      const expiresAt = entry.offer_expires_at ? new Date(entry.offer_expires_at) : now;
      const diff = expiresAt.getTime() - now.getTime();
      const totalHours = diff / (1000 * 60 * 60);

      await waitlistApi.acceptOffer(entry.id);

      // Track acceptance (Issue #498)
      trackSlotOfferAccepted({
        fair_id: entry.fair_id,
        position: entry.position,
        time_remaining_hours: totalHours,
        time_to_decision_seconds: Math.round(timeToDecision / 1000),
      });

      // Success feedback (Issue #500)
      playSound('success');
      vibrateSuccess();

      setSuccess(true);
      toast.success('Reservation confirmed! Check your email for details.');
      setTimeout(() => {
        onAccept?.();
        handleClose();
      }, 1500);
    } catch (err: unknown) {
      const message = extractApiError(err).message;
      setError(message);
      toast.error(message);
      setIsAccepting(false);

      // Error feedback (Issue #500)
      playSound('error');

      // Track error (Issue #498)
      trackError({
        error_type: 'slot_offer_accept_failed',
        error_message: message,
        fair_id: entry.fair_id,
        action: 'accept_offer',
      });
    }
  }, [entry.id, entry.fair_id, entry.position, entry.offer_expires_at, modalOpenTime, onAccept, handleClose, trackSlotOfferAccepted, trackError]);

  const handleDecline = useCallback(async () => {
    try {
      setError(null);
      setIsDeclining(true);

      // Light haptic on click (Issue #500)
      vibrateLight();

      // Calculate time remaining (Issue #498)
      const now = new Date();
      const expiresAt = entry.offer_expires_at ? new Date(entry.offer_expires_at) : now;
      const diff = expiresAt.getTime() - now.getTime();
      const totalHours = diff / (1000 * 60 * 60);

      await waitlistApi.declineOffer(entry.id, declineReason || undefined);

      // Track decline (Issue #498)
      trackSlotOfferDeclined({
        fair_id: entry.fair_id,
        position: entry.position,
        time_remaining_hours: totalHours,
        decline_reason: declineReason || undefined,
      });

      // Success feedback for decline (Issue #500)
      playSound('click');
      vibrateLight();

      toast.success('You have declined the offer and remain on the waitlist.');
      onDecline?.();
      handleClose();
    } catch (err: unknown) {
      const message = extractApiError(err).message;
      setError(message);
      toast.error(message);
      setIsDeclining(false);

      // Error feedback (Issue #500)
      playSound('error');

      // Track error (Issue #498)
      trackError({
        error_type: 'slot_offer_decline_failed',
        error_message: message,
        fair_id: entry.fair_id,
        action: 'decline_offer',
      });
    }
  }, [entry.id, entry.fair_id, entry.position, entry.offer_expires_at, declineReason, onDecline, handleClose, trackSlotOfferDeclined, trackError]);

  const formatDate = useCallback((dateString?: string) => {
    const date = parseDateOnly(dateString); // Issue #1672: handles YYYY-MM-DD as local time
    if (!date) return 'N/A';
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }, []);

  const isExpired = countdownDetails.isExpired;

  // Keyboard navigation - Escape key closes modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isAccepting && !isDeclining) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isAccepting, isDeclining, handleClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="slot-offer-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isAccepting && !isDeclining) {
          handleClose();
        }
      }}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] sm:max-h-[95vh] overflow-y-auto md:rounded-lg">
        {/* Skip link for accessibility (Issue #499) */}
        <a href="#modal-content" className="skip-link">
          Skip to modal content
        </a>

        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 id="slot-offer-modal-title" className="text-lg sm:text-xl font-bold text-gray-900">
            Slot Available!
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
            disabled={isAccepting || isDeclining}
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Success State */}
        {success ? (
          <div className="p-6 text-center">
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Reservation Confirmed!
            </h3>
            <p className="text-gray-600">
              Check your email for confirmation details.
            </p>
          </div>
        ) : (
          <>
            {/* Body */}
            <div id="modal-content" className="p-4 sm:p-6 space-y-4" tabIndex={-1}>
              {/* Timer with Progress Bar */}
              {!isExpired ? (
                <div
                  className={`
                    border rounded-lg p-4 transition-all duration-300
                    ${countdownDetails.colorClass === 'green' ? 'bg-green-50 border-green-200' : ''}
                    ${countdownDetails.colorClass === 'yellow' ? 'bg-yellow-50 border-yellow-200' : ''}
                    ${countdownDetails.colorClass === 'red' ? 'bg-red-50 border-red-200' : ''}
                    ${countdownDetails.isUrgent && !prefersReducedMotion ? 'animate-pulse' : ''}
                  `}
                  role="timer"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Clock
                        className={`w-5 h-5 ${
                          countdownDetails.colorClass === 'green' ? 'text-green-600' :
                          countdownDetails.colorClass === 'yellow' ? 'text-yellow-600' :
                          'text-red-600'
                        }`}
                      />
                      <span
                        className={`font-medium ${
                          countdownDetails.colorClass === 'green' ? 'text-green-900' :
                          countdownDetails.colorClass === 'yellow' ? 'text-yellow-900' :
                          'text-red-900'
                        }`}
                      >
                        Time Remaining:
                      </span>
                    </div>
                    <Tooltip
                      content={
                        countdownDetails.colorClass === 'green'
                          ? 'More than 24 hours remaining - plenty of time!'
                          : countdownDetails.colorClass === 'yellow'
                          ? '6-24 hours remaining - respond soon'
                          : 'Less than 6 hours - urgent action needed!'
                      }
                      position="left"
                    >
                      <span
                        className={`text-lg sm:text-2xl font-bold cursor-help ${
                          countdownDetails.colorClass === 'green' ? 'text-green-600' :
                          countdownDetails.colorClass === 'yellow' ? 'text-yellow-600' :
                          'text-red-600'
                        }`}
                      >
                        {countdownDetails.text}
                      </span>
                    </Tooltip>
                  </div>
                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-1000 ${
                        countdownDetails.colorClass === 'green' ? 'bg-green-600' :
                        countdownDetails.colorClass === 'yellow' ? 'bg-yellow-600' :
                        'bg-red-600'
                      }`}
                      style={{ width: `${countdownDetails.progress}%` }}
                      role="progressbar"
                      aria-valuenow={countdownDetails.progress}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-label="Time elapsed"
                    />
                  </div>
                </div>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                    <span className="font-medium text-red-900">This offer has expired</span>
                  </div>
                </div>
              )}

              {/* Fair Details */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Fair Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Fair Name:</span>
                    <span className="font-medium text-gray-900">{entry.fair_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Dates:</span>
                    <span className="font-medium text-gray-900">
                      {formatDate(entry.fair_start_date)} - {formatDate(entry.fair_end_date)}
                    </span>
                  </div>
                  {entry.slot_preference && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Your Preference:</span>
                      <span className="font-medium text-gray-900">{entry.slot_preference}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Vendor:</span>
                    <span className="font-medium text-gray-900">{entry.vendor_business_name}</span>
                  </div>
                </div>
              </div>

              {/* Instructions */}
              <div className="bg-moonbone border border-teal-200 rounded-lg p-4 text-sm">
                <p className="font-medium text-ink mb-2">What happens next?</p>
                <ul className="list-disc list-inside space-y-1 text-ink">
                  <li>Accept the offer to reserve your slot</li>
                  <li>You'll receive a reservation confirmation</li>
                  <li>Fair managers will finalize your booking</li>
                  <li>Or decline if you're no longer interested</li>
                </ul>
              </div>

              {/* Decline Form */}
              {showDeclineForm && (
                <div className="border border-gray-300 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for declining (optional)
                  </label>
                  <textarea
                    value={declineReason}
                    onChange={(e) => setDeclineReason(e.target.value)}
                    placeholder="Let us know why you're declining..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dusty-teal resize-none"
                    rows={3}
                    maxLength={500}
                  />
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                  {error}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-4 sm:p-6 border-t border-gray-200 bg-gray-50 sticky bottom-0">
              {!showDeclineForm ? (
                <>
                  <button
                    onClick={() => setShowDeclineForm(true)}
                    disabled={isAccepting || isExpired}
                    className="px-4 py-3 sm:py-2 text-gray-700 hover:text-gray-900 font-medium disabled:opacity-50 order-2 sm:order-1 transition-colors"
                    aria-label="Decline offer"
                  >
                    Decline
                  </button>
                  <button
                    onClick={handleAccept}
                    disabled={isAccepting || isExpired}
                    className="px-6 py-3 sm:py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium order-1 sm:order-2 transition-colors shadow-sm hover:shadow-md"
                    aria-label="Accept offer"
                  >
                    {isAccepting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Accepting...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        Accept Offer
                      </>
                    )}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setShowDeclineForm(false)}
                    disabled={isDeclining}
                    className="px-4 py-3 sm:py-2 text-gray-700 hover:text-gray-900 font-medium order-2 sm:order-1 transition-colors"
                    aria-label="Cancel decline"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDecline}
                    disabled={isDeclining}
                    className="px-6 py-3 sm:py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium order-1 sm:order-2 transition-colors shadow-sm hover:shadow-md"
                    aria-label="Confirm decline"
                  >
                    {isDeclining ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Declining...
                      </>
                    ) : (
                      'Confirm Decline'
                    )}
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default SlotOfferModal;
